import React, { useMemo } from 'react';
import { Stack, Text, Group, Avatar, Paper, Badge, Divider } from '@mantine/core';
import { MessageCircle, Clock } from 'lucide-react';
import { useDashboardData } from '../../../../hooks/dashboard';

interface TaskComment {
  taskId: number;
  taskTitle: string;
  latestComment: {
    id: number;
    text: string;
    authorName: string;
    authorEmail: string;
    createdAt: string;
  };
  totalComments: number;
}

interface CommentsWidgetProps {
  onEditTodo?: (todoId: number) => void;
}

export const CommentsWidget: React.FC<CommentsWidgetProps> = ({ onEditTodo }) => {
  const dashboardData = useDashboardData();

  const commentedTasks = useMemo((): TaskComment[] => {
    const tasksWithComments = dashboardData.todos
      .filter(task => task.comments && task.comments.length > 0)
      .map(task => {
        const latestComment = task.comments!
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        return {
          taskId: task.id,
          taskTitle: task.title,
          latestComment: {
            id: latestComment.id,
            text: latestComment.content || '',
            authorName: latestComment.authorEmail ? latestComment.authorEmail.split('@')[0] : 'Unknown',
            authorEmail: latestComment.authorEmail || '',
            createdAt: latestComment.createdAt || ''
          },
          totalComments: task.comments!.length
        };
      })
      .sort((a, b) =>
        new Date(b.latestComment.createdAt).getTime() - new Date(a.latestComment.createdAt).getTime()
      )
      .slice(0, 5);

    return tasksWithComments;
  }, [dashboardData.todos]);

  const formatTimeAgo = (dateString: string): string => {
    const now = new Date();
    const commentTime = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - commentTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return commentTime.toLocaleDateString();
  };

  const getInitials = (name: string | undefined | null): string => {
    if (!name || typeof name !== 'string') {
      return '??';
    }

    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  const generateAvatarColor = (email: string | undefined | null): string => {
    const colors = ['blue', 'green', 'red', 'purple', 'orange', 'teal', 'pink', 'indigo'];

    if (!email || typeof email !== 'string') {
      return colors[0]; // Default to blue
    }

    const hash = email.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  if (commentedTasks.length === 0) {
    return null;
  }

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed" fw={500} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Recent Conversations
      </Text>

      <Stack gap="xs">
        {commentedTasks.map((item) => (
          <Paper
            key={item.taskId}
            p="sm"
            withBorder
            style={{
              backgroundColor: 'var(--app-surface)',
              borderColor: 'var(--app-border)',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            className="comment-task-item"
            onClick={() => onEditTodo?.(item.taskId)}
          >
            <Stack gap="xs">
              {/* Task Title Header */}
              <Group justify="space-between" align="center">
                <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  <MessageCircle
                    size={14}
                    style={{
                      color: 'var(--mantine-color-blue-6)',
                      flexShrink: 0
                    }}
                  />
                  <Text
                    size="sm"
                    fw={600}
                    style={{
                      color: 'var(--app-text-primary)',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.taskTitle}
                  </Text>
                </Group>
                <Badge size="xs" variant="light" color="blue">
                  {item.totalComments}
                </Badge>
              </Group>

              {/* Latest Comment */}
              <Group gap="sm" align="flex-start">
                <Avatar
                  size="sm"
                  radius="xl"
                  color={generateAvatarColor(item.latestComment.authorEmail)}
                  style={{ flexShrink: 0 }}
                >
                  {getInitials(item.latestComment.authorName)}
                </Avatar>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Comment Bubble */}
                  <Paper
                    p="xs"
                    radius="md"
                    style={{
                      backgroundColor: 'var(--mantine-color-gray-0)',
                      border: '1px solid var(--mantine-color-gray-2)',
                      maxWidth: '100%'
                    }}
                  >
                    <Text
                      size="sm"
                      style={{
                        color: 'var(--app-text-primary)',
                        lineHeight: 1.4,
                        wordBreak: 'break-word'
                      }}
                    >
                      {(item.latestComment.text || '').length > 120
                        ? `${(item.latestComment.text || '').substring(0, 120)}...`
                        : (item.latestComment.text || 'No comment text')
                      }
                    </Text>
                  </Paper>

                  {/* Comment Meta */}
                  <Group gap="xs" mt={4}>
                    <Text size="xs" c="dimmed" fw={500}>
                      {item.latestComment.authorName || 'Unknown User'}
                    </Text>
                    <Group gap={4}>
                      <Clock size={10} style={{ color: 'var(--app-text-muted)' }} />
                      <Text size="xs" c="dimmed">
                        {formatTimeAgo(item.latestComment.createdAt)}
                      </Text>
                    </Group>
                  </Group>
                </div>
              </Group>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {commentedTasks.length >= 3 && (
        <>
          <Divider size="xs" color="var(--app-border)" />
          <Text size="xs" c="dimmed" ta="center">
            💬 Stay connected with your team discussions
          </Text>
        </>
      )}
    </Stack>
  );
};